import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles } from "lucide-react";
import { MonitoringTag } from "@/hooks/useMonitoringTags";

interface SuggestedTag {
  name: string;
  category: MonitoringTag['category'];
  color: string;
}

interface SuggestedTagsProps {
  healthArea: string;
  suggestions: SuggestedTag[];
  onAddSelected: (tags: SuggestedTag[]) => void;
}

export function SuggestedTags({ healthArea, suggestions, onAddSelected }: SuggestedTagsProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(suggestions.map(s => s.name)));

  const toggleSelection = (name: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(name)) {
      newSelected.delete(name);
    } else {
      newSelected.add(name);
    }
    setSelected(newSelected);
  };

  const handleAdd = () => {
    const selectedTags = suggestions.filter(s => selected.has(s.name));
    onAddSelected(selectedTags);
  };

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sparkles className="w-5 h-5 text-primary" />
          Tags Sugeridas
        </CardTitle>
        <CardDescription>
          Com base na sua área: <strong>{healthArea}</strong>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {suggestions.map((tag) => (
            <label
              key={tag.name}
              className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition"
              style={{
                borderColor: selected.has(tag.name) ? tag.color : 'transparent',
                backgroundColor: selected.has(tag.name) ? `${tag.color}10` : undefined
              }}
            >
              <Checkbox
                checked={selected.has(tag.name)}
                onCheckedChange={() => toggleSelection(tag.name)}
              />
              <span className="text-sm font-medium">{tag.name}</span>
            </label>
          ))}
        </div>
        <Button 
          onClick={handleAdd} 
          className="w-full"
          disabled={selected.size === 0}
        >
          Adicionar {selected.size} tag{selected.size !== 1 ? 's' : ''} selecionada{selected.size !== 1 ? 's' : ''}
        </Button>
      </CardContent>
    </Card>
  );
}
